import React, {useEffect, useRef, useState} from 'react';
import {config} from 'config';
import {isUuid} from 'helpers/isUuid';
import {isRestErrorWithStatus} from 'hooks';
import useAuthState from 'hooks/useAuthState';
import {useBankIDComplete} from 'hooks/useBankIDComplete';
import {OidcCallbackRequest, useBankIDStatus} from 'hooks/useBankIDStatus';
import {WrongBrowserError} from 'pages/error/WrongBrowserError';
import {StoredBft} from 'services/bft';

const MAX_ERROR_LENGTH = 64;
const MAX_ERROR_DESCRIPTION_LENGTH = 512;

type CallbackParams = {
  state: string | null;
  bft: string | null;
  request: OidcCallbackRequest;
};

// index.html moves the query out of the URL before analytics load and keeps it here
const readCallbackSearch = () => {
  const w = window as Window & {__noBankIdCallback?: string};
  const search = w.__noBankIdCallback || window.location.search;
  delete w.__noBankIdCallback;
  if (window.location.search) {
    window.history.replaceState(null, '', window.location.pathname);
  }
  return new URLSearchParams(search);
};

const readStoredBft = (state: string) => {
  try {
    const stored = localStorage.getItem(`bft-${state}`);
    return stored ? (JSON.parse(stored) as StoredBft).value : null;
  } catch {
    return null;
  }
};

const readCallbackParams = (): CallbackParams => {
  const params = readCallbackSearch();
  const rawState = params.get('state');
  const state = isUuid(rawState) ? rawState : null;
  const code = params.get('code');
  const error = params.get('error');

  let request: OidcCallbackRequest;
  if (code && !error) {
    request = {code};
  } else {
    request = {
      error: (error || 'invalid_request').slice(0, MAX_ERROR_LENGTH),
      error_description:
        params
          .get('error_description')
          ?.slice(0, MAX_ERROR_DESCRIPTION_LENGTH) ?? undefined,
    };
  }

  return {state, bft: state ? readStoredBft(state) : null, request};
};

export const LoginNoBankIdCallbackPage = () => {
  const [{state, bft, request}] = useState(readCallbackParams);
  const auth = useAuthState('NO');
  const entrypointTokenMutation = auth.getEntrypointAccessToken;
  const accessToken = entrypointTokenMutation.data?.accessToken;
  const callbackCalledRef = useRef(false);

  const bankIDStatus = useBankIDStatus({
    token: state,
    authToken: accessToken,
    bft,
    statusEnabled: false,
    host: config.bankIdHost,
  });
  const {oidcCallback, createBrowserFixation} = bankIDStatus;
  const {complete} = useBankIDComplete({
    token: state,
    authToken: accessToken,
    bft,
    host: config.bankIdHost,
  });
  const isWrongBrowserError =
    !state ||
    !bft ||
    isRestErrorWithStatus(oidcCallback.error, [400]) ||
    !!(createBrowserFixation.error ?? complete.error);

  useEffect(() => {
    if (
      !isWrongBrowserError &&
      !entrypointTokenMutation.data &&
      !entrypointTokenMutation.isPending &&
      !entrypointTokenMutation.isError
    ) {
      entrypointTokenMutation.mutate(undefined);
    }
  }, [entrypointTokenMutation, isWrongBrowserError]);

  useEffect(() => {
    if (!accessToken || !state || !bft || callbackCalledRef.current) return;
    callbackCalledRef.current = true;

    (async () => {
      const result = await oidcCallback.mutateAsync(request);
      if (result.authType === 'DIRECT') {
        const redirectUrl = await complete.mutateAsync();
        localStorage.removeItem(`bft-${state}`);
        window.location.replace(redirectUrl);
      } else if (result.status === 'COMPLETED') {
        const fixation = await createBrowserFixation.mutateAsync(result.authId);
        localStorage.removeItem(`bft-${state}`);
        window.location.replace(
          auth.getPreauthorizationURL(
            fixation.authId,
            'completed',
            fixation.bpt,
          ),
        );
      } else {
        localStorage.removeItem(`bft-${state}`);
        window.location.replace(
          auth.getPreauthorizationURL(
            result.authId,
            'canceled',
            undefined,
            result.status,
          ),
        );
      }
    })().catch(() => {
      // Surfaced through the mutation errors
    });
  }, [
    accessToken,
    state,
    bft,
    request,
    oidcCallback,
    createBrowserFixation,
    complete,
    auth,
  ]);

  if (isWrongBrowserError) {
    return <WrongBrowserError />;
  }
  if (oidcCallback.error) throw oidcCallback.error;
  if (entrypointTokenMutation.error) throw entrypointTokenMutation.error;

  return null;
};
